/**
 * InstallPrompt — Smart PWA install banner
 * 
 * Detects if the app is running in a browser tab (not installed as PWA)
 * and shows a dismissible banner prompting installation.
 * 
 * - Android/Chrome: Uses beforeinstallprompt for one-tap install
 * - iOS Safari: Shows manual instructions
 * - Remembers dismissal via localStorage (re-shows after 7 days)
 */
import { useState, useEffect, useRef } from 'react';
import { Download, X, Share } from 'lucide-react';

const DISMISS_KEY = 'alwaysin_install_dismissed';
const DISMISS_DAYS = 7;

function isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches
        || window.navigator.standalone === true;
}

function isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

export default function InstallPrompt() {
    const [show, setShow] = useState(false);
    const [showIOSGuide, setShowIOSGuide] = useState(false);
    const deferredPrompt = useRef(null);

    useEffect(() => {
        // Don't show if already installed as PWA
        if (isStandalone()) return;

        // Check if dismissed recently
        const dismissed = localStorage.getItem(DISMISS_KEY);
        if (dismissed) {
            const dismissedAt = parseInt(dismissed, 10);
            const daysSince = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
            if (daysSince < DISMISS_DAYS) return;
        }

        // Capture the beforeinstallprompt event (Chrome/Edge/Android)
        // This event ONLY fires when the app is NOT already installed
        const handler = (e) => {
            e.preventDefault();
            deferredPrompt.current = e;
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', handler);

        // Auto-dismiss if user installs during this session
        const installedHandler = () => {
            setShow(false);
            deferredPrompt.current = null;
        };
        window.addEventListener('appinstalled', installedHandler);

        // On iOS, no beforeinstallprompt — show after a short delay
        if (isIOS()) {
            const timer = setTimeout(() => setShow(true), 3000);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('beforeinstallprompt', handler);
                window.removeEventListener('appinstalled', installedHandler);
            };
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handler);
            window.removeEventListener('appinstalled', installedHandler);
        };
    }, []);

    const handleInstall = async () => {
        if (isIOS()) {
            setShowIOSGuide(true);
            return;
        }

        if (deferredPrompt.current) {
            deferredPrompt.current.prompt();
            const result = await deferredPrompt.current.userChoice;
            if (result.outcome === 'accepted') {
                setShow(false);
            }
            deferredPrompt.current = null;
        }
    };

    const handleDismiss = () => {
        localStorage.setItem(DISMISS_KEY, Date.now().toString());
        setShow(false);
        setShowIOSGuide(false);
    };

    if (!show) return null;

    return (
        <div className="install-prompt">
            {showIOSGuide ? (
                <div className="install-prompt__guide">
                    <button className="install-prompt__close" onClick={handleDismiss} aria-label="Dismiss">
                        <X size={16} />
                    </button>
                    <div className="install-prompt__steps">
                        <p><strong>Install AlwaysIn</strong></p>
                        <p>1. Tap the <Share size={14} style={{ verticalAlign: 'middle' }} /> <strong>Share</strong> button</p>
                        <p>2. Scroll down and tap <strong>Add to Home Screen</strong></p>
                        <p>3. Tap <strong>Add</strong> to confirm</p>
                    </div>
                </div>
            ) : (
                <div className="install-prompt__banner">
                    <div className="install-prompt__content">
                        <Download size={18} />
                        <div>
                            <strong>Install AlwaysIn</strong>
                            <span className="install-prompt__subtext">Get reliable notifications & quick access</span>
                        </div>
                    </div>
                    <div className="install-prompt__actions">
                        <button className="install-prompt__btn" onClick={handleInstall}>
                            Install
                        </button>
                        <button className="install-prompt__close" onClick={handleDismiss} aria-label="Dismiss">
                            <X size={16} />
                        </button>
                    </div>
                </div>
            )}

            <style>{`
                .install-prompt {
                    position: fixed;
                    bottom: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    z-index: 10000;
                    max-width: 420px;
                    width: calc(100% - 32px);
                    animation: installSlideUp 0.4s ease-out;
                }
                @keyframes installSlideUp {
                    from { transform: translateX(-50%) translateY(100px); opacity: 0; }
                    to { transform: translateX(-50%) translateY(0); opacity: 1; }
                }
                .install-prompt__banner,
                .install-prompt__guide {
                    background: var(--bg-card, #1a1f2e);
                    border: 1px solid var(--border-color, rgba(255,255,255,0.1));
                    border-radius: 14px;
                    padding: 14px 16px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.4);
                    backdrop-filter: blur(12px);
                }
                .install-prompt__guide {
                    flex-direction: column;
                    align-items: flex-start;
                    position: relative;
                    padding-top: 18px;
                }
                .install-prompt__steps p {
                    margin: 4px 0;
                    font-size: 13px;
                    color: var(--text-secondary, #a0a8c0);
                }
                .install-prompt__steps p:first-child {
                    color: var(--text-primary, #fff);
                    font-size: 14px;
                    margin-bottom: 8px;
                }
                .install-prompt__content {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex: 1;
                    color: var(--text-primary, #fff);
                }
                .install-prompt__content strong {
                    display: block;
                    font-size: 14px;
                }
                .install-prompt__subtext {
                    font-size: 12px;
                    color: var(--text-secondary, #a0a8c0);
                    display: block;
                    margin-top: 2px;
                }
                .install-prompt__actions {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-shrink: 0;
                }
                .install-prompt__btn {
                    background: var(--accent, #6c5ce7);
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 8px 16px;
                    font-size: 13px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: opacity 0.2s;
                }
                .install-prompt__btn:hover { opacity: 0.85; }
                .install-prompt__close {
                    background: transparent;
                    border: none;
                    color: var(--text-secondary, #a0a8c0);
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    border-radius: 6px;
                    transition: color 0.2s;
                }
                .install-prompt__close:hover { color: var(--text-primary, #fff); }
                .install-prompt__guide .install-prompt__close {
                    position: absolute;
                    top: 8px;
                    right: 8px;
                }
            `}</style>
        </div>
    );
}
